/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Terms of Service page — required for Google OAuth branding verification.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { Box, Container, Typography, Divider, Stack } from '@mui/material';
import { useThemeContext } from '@/contexts/ThemeContext';
import { createPageMeta } from '@/lib/meta';

const LAST_UPDATED = 'February 28, 2026';

const CONTACT_EMAIL: string | null = null;

export const Route = createFileRoute('/terms-of-service')({
  component: TermsOfServicePage,
  head: () => createPageMeta({
    title: 'Terms of Service - Christian Battaglia',
    description: 'Terms of service for cdbattaglia.com - rules and guidelines for using this site.',
    path: '/terms-of-service',
    image: '/og/terms-of-service.png',
  }),
});

function ContactInfo() {
  if (CONTACT_EMAIL) {
    return <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
  }
  return <em>Contact email coming soon</em>;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="h5" component="h2" fontWeight={700} sx={{ mt: 5, mb: 2 }}>
      {children}
    </Typography>
  );
}

function TermsOfServicePage() {
  const { mode } = useThemeContext();
  const linkColor = mode === 'dark' ? '#60a5fa' : '#1e3a8a';

  return (
    <Box sx={{ minHeight: 'calc(100vh - 70px)', bgcolor: 'background.default' }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Typography variant="h3" component="h1" fontWeight={800} sx={{ mb: 1 }}>
          Terms of Service
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: {LAST_UPDATED}
        </Typography>
        <Divider sx={{ mb: 4 }} />

        <Stack spacing={0} sx={{
          '& p': { mb: 2, lineHeight: 1.8 },
          '& ul': { pl: 3, mb: 2 },
          '& li': { mb: 1, lineHeight: 1.7 },
          '& a': { color: linkColor, textDecoration: 'underline' },
        }}>
          {/* 1. Acceptance */}
          <SectionHeading>1. Acceptance of Terms</SectionHeading>
          <Typography>
            By accessing or using <strong>cdbattaglia.com</strong> (the "Site"), you agree to be bound
            by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use
            the Site. These Terms should be read alongside our{' '}
            <Link to="/privacy-policy" style={{ color: linkColor }}>Privacy Policy</Link>.
          </Typography>

          {/* 2. Description */}
          <SectionHeading>2. Description of Service</SectionHeading>
          <Typography>
            The Site is a personal portfolio and blog operated by Christian Battaglia. It offers:
          </Typography>
          <ul>
            <li><Typography component="span">Blog posts and technical articles</Typography></li>
            <li><Typography component="span">A music analysis tool with optional Spotify integration</Typography></li>
            <li><Typography component="span">A project showcase and open-source portfolio</Typography></li>
            <li><Typography component="span">Interactive features including a command palette, theme customization, and easter eggs</Typography></li>
          </ul>
          <Typography>
            All features are provided free of charge and on an "as-is" basis.
          </Typography>

          {/* 3. User Accounts / Authentication */}
          <SectionHeading>3. User Accounts and Authentication</SectionHeading>
          <Typography>
            Certain features may require authentication through third-party providers:
          </Typography>
          <ul>
            <li>
              <Typography component="span">
                <strong>Spotify</strong> — connecting your Spotify account authorizes the Site to access
                your email, display name, and music library for the music analysis feature. You can
                disconnect at any time.
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Google Sign-In</strong> — signing in with Google grants the Site access to your
                email and profile name, used solely for authentication and access to gated features
                like the contact page.
              </Typography>
            </li>
          </ul>
          <Typography>
            You are responsible for the security of your third-party accounts. We do not store passwords.
          </Typography>

          {/* 4. Acceptable Use */}
          <SectionHeading>4. Acceptable Use</SectionHeading>
          <Typography>When using the Site, you agree <strong>not</strong> to:</Typography>
          <ul>
            <li><Typography component="span">Attempt to gain unauthorized access to any part of the Site or its systems</Typography></li>
            <li><Typography component="span">Use automated tools, bots, or scrapers beyond reasonable use</Typography></li>
            <li><Typography component="span">Interfere with or disrupt the Site's functionality</Typography></li>
            <li><Typography component="span">Reverse-engineer or attempt to extract source code not publicly available</Typography></li>
            <li><Typography component="span">Use the Site in any way that violates applicable laws</Typography></li>
          </ul>

          {/* 5. Intellectual Property */}
          <SectionHeading>5. Intellectual Property</SectionHeading>
          <Typography>
            All content on this Site — including text, code, designs, images, and the Site's overall
            look and feel — is the intellectual property of Christian Battaglia unless otherwise noted.
          </Typography>
          <Typography>
            Blog posts and articles are published for educational purposes. You may share or reference
            them with proper attribution. You may not reproduce them in bulk or use them commercially
            without permission.
          </Typography>
          <Typography>
            Open-source projects showcased on this Site are governed by their respective licenses
            (typically MIT or similar). Refer to each project's repository for specific license terms.
          </Typography>

          {/* 6. Third-Party Services */}
          <SectionHeading>6. Third-Party Services</SectionHeading>
          <Typography>
            The Site integrates with third-party services (Spotify, Google, PostHog) that are governed
            by their own terms and privacy policies. Your use of these services through the Site is
            subject to their respective terms:
          </Typography>
          <ul>
            <li>
              <Typography component="span">
                <a href="https://www.spotify.com/legal/end-user-agreement/" target="_blank" rel="noopener noreferrer">
                  Spotify Terms of Use
                </a>
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                  Google Terms of Service
                </a>
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <a href="https://posthog.com/terms" target="_blank" rel="noopener noreferrer">
                  PostHog Terms of Service
                </a>
              </Typography>
            </li>
          </ul>

          {/* 7. Disclaimer */}
          <SectionHeading>7. Disclaimer of Warranties</SectionHeading>
          <Typography>
            THE SITE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
            IMPLIED, INCLUDING BUT NOT LIMITED TO FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT,
            OR ACCURACY OF CONTENT.
          </Typography>
          <Typography>
            The music analysis tool provides estimates and should not be considered authoritative for
            professional audio work. Blog content reflects personal opinions and may become outdated.
          </Typography>

          {/* 8. Limitation of Liability */}
          <SectionHeading>8. Limitation of Liability</SectionHeading>
          <Typography>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, CHRISTIAN BATTAGLIA SHALL NOT BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE
            OF THE SITE, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, UNAUTHORIZED ACCESS TO YOUR
            THIRD-PARTY ACCOUNTS, OR INTERRUPTION OF SERVICE.
          </Typography>

          {/* 9. Changes */}
          <SectionHeading>9. Changes to These Terms</SectionHeading>
          <Typography>
            We reserve the right to update these Terms at any time. When we do, we will update the
            "Last updated" date at the top of this page. Continued use of the Site after changes
            constitutes acceptance of the updated Terms.
          </Typography>

          {/* 10. Governing Law */}
          <SectionHeading>10. Governing Law</SectionHeading>
          <Typography>
            These Terms are governed by and construed in accordance with the laws of the United States.
            Any disputes arising from these Terms will be resolved in the courts of the state in which
            the operator resides.
          </Typography>

          {/* 11. Contact */}
          <SectionHeading>11. Contact</SectionHeading>
          <Typography>
            If you have any questions about these Terms, please contact
            us at: <ContactInfo />.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
